import { useState } from "react";

import useContractPreview from "./use-contract-preview";
import usePdfViewer from "./use-pdf-viewer";
import { PREVIEW_KIND, VIEWER_STATUS } from "../misc/constants";
import { previewKindOfType } from "../misc/helpers";

/**
 * A read-only preview of any /document route, in the same embedded reader the
 * review modal uses but without the acknowledgement. For the screens that only
 * need to look at a contract — ops checking the brand's agreement or one
 * publisher's copy — instead of handing it to a new tab.
 *
 * `openPreview` takes the route and what the modal should call the document;
 * closing it releases the bytes.
 * @param base the useAxios base for the role: ops | brand | affiliate
 */
const useDocumentViewer = (base) => {
    // { path, name, subtitle } of the document on screen, or null.
    const [target, setTarget] = useState(null);
    const active = Boolean(target);

    const preview = useContractPreview({ base, path: target?.path || "", active });
    const kind = preview.url ? previewKindOfType(preview.type) : PREVIEW_KIND.PDF;
    const pdfViewer = usePdfViewer({ url: preview.url, kind, active });

    // A failed document request reads as the frame failing: same fallback,
    // with the server's reason in place of the generic one.
    const viewer = {
        ...pdfViewer,
        loading: preview.loading,
        error  : preview.error,
        status : preview.error ? VIEWER_STATUS.ERROR : pdfViewer.status,
        retry  : preview.error ? preview.retry : pdfViewer.retry
    };

    return {
        openPreview: (path, name, subtitle = "") => path && setTarget({ path, name, subtitle }),
        modal      : {
            open    : active,
            contract: target && {
                name       : target.name,
                url        : preview.url,
                previewKind: kind
            },
            subtitle: target?.subtitle || "",
            viewer,
            onClose : () => setTarget(null)
        }
    };
};

export default useDocumentViewer;
